import type { ReadonlySignal } from '@preact/signals-core';
import { computed } from '@preact/signals-core';

import type { DataSource } from './base.js';

const MAX_PROJECTION_CACHE_ENTRIES = 32;

type ProjectionCacheEntry<T> = {
  readonly value$: ReadonlySignal<T>;
};

const projectionCaches = new WeakMap<
  object,
  Map<string, ProjectionCacheEntry<unknown>>
>();
const performanceScopes = new WeakMap<DataSource, object>();

function canonicalProjectionValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalProjectionValue);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonicalProjectionValue(entry)])
    );
  }
  return value;
}

export function getDataSourcePerformanceScope(
  dataSource: DataSource
): object | undefined {
  return performanceScopes.get(dataSource);
}

export function getOrCreateProjectionCacheEntry<T>(
  owner: object,
  key: string,
  create: () => T
): ReadonlySignal<T> {
  const cache = projectionCaches.get(owner) ?? new Map();
  projectionCaches.set(owner, cache);
  const existing = cache.get(key) as ProjectionCacheEntry<T> | undefined;
  if (existing) {
    cache.delete(key);
    cache.set(key, existing as ProjectionCacheEntry<unknown>);
    return existing.value$;
  }
  const entry: ProjectionCacheEntry<T> = { value$: computed(create) };
  cache.set(key, entry as ProjectionCacheEntry<unknown>);
  while (cache.size > MAX_PROJECTION_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  return entry.value$;
}

export function projectionCacheKey(viewId: string, query: unknown): string {
  return `${viewId}:${JSON.stringify(canonicalProjectionValue(query))}`;
}

export function setDataSourcePerformanceScope(
  dataSource: DataSource,
  scope: object
): void {
  performanceScopes.set(dataSource, scope);
}
